import type { ComponentRef } from '@angular/core';
import type { Location } from '@freelancer/location';
import type * as Rx from 'rxjs';
import type { ModalCloseConfig, ModalResult } from './modal.types';

export class ModalRef<T> {
  private _afterOpenPromiseResolver: () => void;
  private _afterClosedPromiseResolver: (value: ModalResult) => void;
  private _beforeClosedPromiseResolver: () => void;

  /** Promise that resolves when the modal finishes opening. */
  private _afterOpenPromise = new Promise<void>(resolve => {
    this._afterOpenPromiseResolver = resolve;
  });

  /** Promise that resolves before the modal closes */
  private _beforeClosedPromise = new Promise<void>(resolve => {
    this._beforeClosedPromiseResolver = resolve;
  });

  /** Promise that resolves after the modal finishes closing and returns a result */
  private _afterClosedPromise = new Promise<ModalResult>(resolve => {
    this._afterClosedPromiseResolver = resolve;
  });

  private componentRef: ComponentRef<T>;
  private closed = false;

  private modalPopCloseSubscription?: Rx.SubscriptionLike;
  private result: ModalResult;
  private fromRouteChange: boolean;

  constructor(
    private _openStreamSubject$: Rx.Subject<any>,
    private location: Location,
  ) {}

  open() {
    this.modalPopCloseSubscription = this.location._createBackButtonState({
      onPop: () => this.close(undefined, { fromRouteChange: true }),
    });
  }

  set(componentRef: ComponentRef<T>) {
    this.componentRef = componentRef;
    this._afterOpenPromiseResolver();
  }

  close(
    result?: ModalResult,
    { fromRouteChange = false }: ModalCloseConfig = {},
  ): void {
    // already closed: don't close again.
    if (this.closed) {
      return;
    }
    this.closed = true;
    this.fromRouteChange = fromRouteChange;
    this.result = result;
    this._beforeClosedPromiseResolver();
  }

  _destroy(): void {
    // if the modal failed to load (e.g. network error), we might not have a
    // componentRef
    if (this.componentRef) {
      this.componentRef.destroy();
    }

    if (this.modalPopCloseSubscription) {
      this.modalPopCloseSubscription.unsubscribe();
    }

    // close `openStreamSubject` immediately
    this._openStreamSubject$.next(false);

    if (!this.fromRouteChange) {
      this.location.back().then(() => {
        // emit to afterClosed after back-navigation completes
        this._afterClosedPromiseResolver(this.result);
      });
      return;
    }

    this._afterClosedPromiseResolver(this.result);
  }

  /**
   * Observable to tell the modal component to start running the hide animation
   * NOTE: This is private and should not be used by app code
   */
  _beforeClose(): Promise<void> {
    return this._beforeClosedPromise;
  }

  afterOpen(): Promise<void> {
    return this._afterOpenPromise;
  }

  afterClosed(): Promise<ModalResult> {
    return this._afterClosedPromise;
  }
}
